import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlusCircle, TrendingUp, Zap, Settings2, MapPin, GraduationCap, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MemberLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

export default function MemberHome() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [targets, setTargets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [quickCount, setQuickCount] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [pulse, setPulse] = useState(0);

  const reload = async () => {
    const s = await api.get("/stats/me"); setStats(s.data);
  };

  useEffect(() => {
    reload();
    api.get("/targets").then(r => setTargets(r.data));
    api.get("/locations").then(r => setLocations(r.data));
    api.get("/activity-types").then(r => setActivityTypes(r.data));
  }, []);

  useEffect(() => {
    if (user?.default_count) setQuickCount(user.default_count);
  }, [user]);

  const favLoc = locations.find(l => l.id === user?.default_location_id);
  const favType = activityTypes.find(a => a.id === user?.default_activity_type_id);
  const canQuick = !!(favLoc && favType);

  const quickRegister = async (increment) => {
    if (!canQuick || submitting) return;
    const value = increment ?? quickCount;
    setSubmitting(true);
    setPulse(p => p + 1);
    try {
      await api.post("/registrations", {
        activity_type_id: favType.id,
        location_id: favLoc.id,
        conversation_count: value,
        activity_date: new Date().toISOString().slice(0, 10),
      });
      await reload();
      toast.success(`+${value} samtal registrerade`, { description: `${favType.name} · ${favLoc.name}` });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunde inte registrera");
    } finally { setSubmitting(false); }
  };

  const national = targets.find(t => t.target_type === "national");

  return (
    <MemberLayout>
      <div className="space-y-6">
        <div>
          <p className="text-slate-500 text-sm">Välkommen,</p>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900" data-testid="member-name">{user?.first_name}</h1>
        </div>

        {/* ----- Quality-conversations info card ----- */}
        <div className="rounded-2xl border border-rose-100 bg-gradient-to-br from-rose-50 to-white p-5" data-testid="quality-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#E1251B] text-white flex items-center justify-center flex-shrink-0">
              <GraduationCap size={20}/>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-bold text-slate-900">Viktig information</h3>
              <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">
                Vad roligt att du kampanjar för Socialdemokraterna! Det är viktigt att vi hjälps åt med att de samtal som registreras är kvalitativa snarare än många. Läs gärna mer om eller träna dig på att föra kvalitativa samtal i vår 4K-tränare på Rosen för alla.
              </p>
              <a
                href="https://rosenforalla.se/rosenforalla/val2026/fyraksamtalstraning"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quality-link"
                className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-[#E1251B] hover:underline"
              >
                Öppna 4K-samtalsträningen <ExternalLink size={13}/>
              </a>
              <div className="text-[11px] text-slate-500 mt-1">Kräver medlemskap och BankID</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Mina samtal</div>
          <div className="font-heading text-6xl font-extrabold text-slate-900 mt-2 number-tabular relative" data-testid="member-total">
            {stats?.total_conversations ?? "—"}
            {pulse > 0 && (
              <span key={pulse} className="absolute -right-4 top-2 text-emerald-600 text-lg font-bold animate-in fade-in slide-in-from-bottom-2 duration-500">
                ↑
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-6 pt-6 border-t border-slate-100">
            <div>
              <div className="text-xs text-slate-500">Denna vecka</div>
              <div className="font-heading text-xl font-bold text-slate-900 number-tabular" data-testid="member-week">{stats?.week_conversations ?? "—"}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Denna månad</div>
              <div className="font-heading text-xl font-bold text-slate-900 number-tabular" data-testid="member-month">{stats?.month_conversations ?? "—"}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Aktiviteter</div>
              <div className="font-heading text-xl font-bold text-slate-900 number-tabular" data-testid="member-activities">{stats?.total_activities ?? "—"}</div>
            </div>
          </div>
        </div>

        {/* ----- Annan typ av aktivitet (order 1) ----- */}
        <Button data-testid="cta-register-activity" onClick={() => nav("/registrera")}
          variant="outline"
          className="w-full h-14 text-base font-semibold rounded-full border-slate-300">
          <PlusCircle className="mr-2" size={20}/> KLICKA HÄR FÖR ATT REGISTRERA SAMTAL
        </Button>

        {/* ----- QUICK REGISTER WIDGET (order 2) ----- */}
        {canQuick ? (
          <div className="rounded-2xl bg-slate-900 text-white p-6" data-testid="quick-register">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase tracking-widest font-semibold opacity-70 flex items-center gap-1.5"><Zap size={12}/> Snabbregistrering</div>
              <button data-testid="quick-settings" onClick={() => nav("/profil")} className="text-white/60 hover:text-white text-xs inline-flex items-center gap-1"><Settings2 size={12}/> Ändra</button>
            </div>
            <div className="flex items-center gap-1.5 mt-1"><MapPin size={14} className="opacity-70"/> <span className="font-heading font-bold text-lg">{favLoc.name}</span></div>
            <div className="text-xs opacity-70">{favType.name}</div>

            <button
              data-testid="quick-plus-one"
              onClick={() => quickRegister(quickCount)}
              disabled={submitting}
              className={cn(
                "mt-5 w-full rounded-2xl bg-[#E1251B] hover:bg-[#C81D14] active:scale-[0.98] transition-all duration-150 text-white font-heading font-extrabold py-6 flex items-center justify-center gap-3 shadow-lg shadow-red-900/30",
                submitting && "opacity-60"
              )}>
              <PlusCircle size={26}/>
              <span className="text-3xl number-tabular">+{quickCount}</span>
              <span className="text-sm font-semibold opacity-90 uppercase tracking-widest">samtal</span>
            </button>

            <div className="grid grid-cols-4 gap-2 mt-3">
              {[1, 3, 5, 10].map(n => (
                <button key={n} data-testid={`quick-preset-${n}`} onClick={() => setQuickCount(n)}
                  className={cn("rounded-full py-2 text-sm font-semibold transition-colors",
                    quickCount === n ? "bg-white text-slate-900" : "bg-white/10 hover:bg-white/20 text-white")}>
                  +{n}
                </button>
              ))}
            </div>
            <p className="text-[11px] opacity-60 mt-3 text-center">Ett tryck = {quickCount} samtal registrerat direkt</p>
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-6" data-testid="quick-setup">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center flex-shrink-0"><Zap size={18}/></div>
              <div className="flex-1">
                <div className="font-heading font-bold text-slate-900">Aktivera snabbregistrering</div>
                <p className="text-sm text-slate-600 mt-1">Välj din favoritplats och aktivitetstyp så registrerar du samtal från hem-vyn med ett tryck.</p>
                <Button data-testid="quick-setup-go" onClick={() => nav("/profil")} variant="outline" size="sm" className="mt-3 rounded-full">Ställ in favoriter →</Button>
              </div>
            </div>
          </div>
        )}

        {national && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Kampanjmål</div>
                <div className="font-heading text-xl font-bold text-slate-900 mt-1">{national.name}</div>
              </div>
              <TrendingUp className="text-[#E1251B]" size={20}/>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-3xl font-extrabold text-slate-900 number-tabular">{national.current?.toLocaleString("sv-SE")}</span>
              <span className="text-slate-500">/ {national.target_conversations.toLocaleString("sv-SE")}</span>
            </div>
            <Progress value={Math.min(100, (national.current/national.target_conversations)*100)} className="mt-3 h-2"/>
            <div className="text-xs text-slate-500 mt-2">{Math.round(Math.min(100,(national.current/national.target_conversations)*100))}% av målet</div>
          </div>
        )}
      </div>
    </MemberLayout>
  );
}
